import React, { useEffect, useMemo, useRef } from "react";
import { Divider, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import { CssTextField } from "../../components/TextField";
import {
    cellOf,
    dayWiseMismatch,
    extrasTotal,
    isBadEntry,
    NIGHT_FIELDS,
    nightsKey,
    nightsOf,
    round2,
    splitAcrossRooms,
} from "./mokshaDayWise";

const NightRow = ({ date, cell, onChange, disabled }) => (
    <Stack direction="row" spacing={1} alignItems="center">
        <Typography width={"40%"} flexShrink={0} fontSize={"small"} color={"#aaa"} pl={2}>
            {moment.unix(date).format("DD MMM YYYY")}
        </Typography>
        {NIGHT_FIELDS.map(({ field, label, placeholder }) => (
            <CssTextField
                key={field}
                fullWidth
                size="small"
                type="number"
                label={label}
                placeholder={placeholder}
                value={cell[field] ?? ""}
                onChange={(e) => onChange(field, e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{ inputProps: { min: 0, step: "any" } }}
                error={isBadEntry(cell[field])}
                disabled={disabled}
            />
        ))}
    </Stack>
);

function MokshaDayWiseRates({ rooms = [], entered = {}, setEntered, specialPrice, disabled, colors }) {
    const { price, total, mismatch } = dayWiseMismatch(rooms, entered, specialPrice);
    const key = useMemo(() => nightsKey(rooms), [rooms]);
    const appliedFor = useRef(null);
    // Unset means GST on, as the backend treats it.
    const gstEnabled = useSelector((state) => state.registration.hotelProfile?.gst_enabled) !== false;
    const taxNote = gstEnabled ? " (incl. tax)" : "";

    const extras = extrasTotal(rooms, entered);

    // Re-splits the room amounts when the price or the set of nights changes; edits made
    // afterwards, extras included, stand.
    useEffect(() => {
        const signature = price > 0 ? `${price}|${key}` : null;
        // Nothing is written while disabled, so clear the mark or re-enabling keeps a stale split.
        if (disabled) {
            if (appliedFor.current !== signature) appliedFor.current = null;
            return;
        }
        if (appliedFor.current === signature) return;

        appliedFor.current = signature;
        const roomTotal = signature ? round2(price - extras) : 0;
        setEntered(splitAcrossRooms(rooms, roomTotal, entered));
        // eslint-disable-next-line
    }, [price, key, disabled]);

    if (!rooms.length) return null;

    const setNight = (roomIndex, date, field, value) =>
        setEntered({
            ...entered,
            [roomIndex]: {
                ...entered[roomIndex],
                [date]: { ...cellOf(entered, roomIndex, date), [field]: value },
            },
        });

    return (
        <Stack spacing={1.5}>
            <Divider />
            <Typography fontWeight={600}>Day-wise rates{taxNote}</Typography>
            <Typography fontSize={"small"} color={"#aaa"}>
                {price > 0
                    ? "Room amounts are split from the special price less extras. Edit any night; room + extra bed + meal must still match."
                    : "Enter a special price above to split it across the nights."}
            </Typography>

            {rooms.map((room, roomIndex) => (
                <Stack key={room?.room_id ?? roomIndex} spacing={1}>
                    <Typography fontSize={"small"} fontWeight={600}>
                        Room {room?.room_no ?? roomIndex + 1}
                        {room?.roomtype ? ` · ${room.roomtype}` : ""}
                    </Typography>
                    {nightsOf(room).map((date) => (
                        <NightRow
                            key={date}
                            date={date}
                            cell={cellOf(entered, roomIndex, date)}
                            onChange={(field, value) => setNight(roomIndex, date, field, value)}
                            disabled={disabled}
                        />
                    ))}
                </Stack>
            ))}

            {extras > 0 ? (
                <Stack direction="row" justifyContent="space-between">
                    <Typography fontSize={"small"} color={"#aaa"}>
                        Extras (extra bed + meal){taxNote}
                    </Typography>
                    <Typography fontSize={"small"}>₹{extras.toFixed(2)}</Typography>
                </Stack>
            ) : null}
            <Stack direction="row" justifyContent="space-between">
                <Typography fontSize={"small"} color={"#aaa"}>
                    Total{taxNote}
                </Typography>
                <Typography
                    fontWeight={600}
                    color={mismatch ? colors?.redAccent?.[400] : colors?.greenAccent?.[500]}
                >
                    ₹{total.toFixed(2)}
                </Typography>
            </Stack>
            {mismatch ? (
                <Typography fontSize={"small"} color={colors?.redAccent?.[400]}>
                    Day-wise total is ₹{total.toFixed(2)} but the special price is ₹{price.toFixed(2)}.
                </Typography>
            ) : null}
            <Divider />
        </Stack>
    );
}

export default MokshaDayWiseRates;
